/**
 * Telemetria mínima de aprendizado do beta (rodada 2B2) — as regras puras.
 *
 * Responde a UMA pergunta que nenhuma tabela do produto responde: "quem
 * abriu o app, e que áreas abriu?". Tudo o que é ação real (atividade,
 * despesa, voto, foto, feedback, incidente) já vive nas tabelas canônicas e
 * NÃO é duplicado aqui — o relatório do beta lê de lá.
 *
 * Três decisões que organizam o módulo, as mesmas do diagnóstico:
 *
 *   1. **Contrato fechado.** Dois eventos, uma lista fixa de áreas, oito
 *      chaves. Chave desconhecida recusa o evento inteiro; não existe
 *      `metadata`, `properties` nem JSON livre — nem no cliente, nem no
 *      servidor, nem na tabela (`product_event` só tem colunas tipadas).
 *   2. **Nada de conteúdo.** Não há campo para texto, valor, rota, URL,
 *      coordenada ou aparelho. `online` e `standalone` são os únicos sinais
 *      de contexto, e são booleanos: com eles se infere uso offline e uso
 *      instalado sem evento próprio (no iOS `appinstalled` não é confiável).
 *   3. **Identidade é da sessão.** `userId` nunca vem do corpo; `tripId` é
 *      só uma indicação que o servidor confere contra a participação.
 *
 * Validação idêntica no cliente (antes de enfileirar) e no servidor (antes
 * de gravar): nenhum dos dois confia no outro.
 */

/* ───────────────────────────── contrato ───────────────────────────── */

export const PRODUCT_EVENT_NAMES = ['app_opened', 'feature_opened'] as const;
export type ProductEventName = (typeof PRODUCT_EVENT_NAMES)[number];

/**
 * As áreas REAIS do app, pela navegação vigente (`apps/web/src/app/routes.tsx`):
 * as cinco abas menos "Mais" (é um menu, não uma área), as telas que vivem
 * dentro do Mais e do drawer da conta, e a folha de feedback (ponto de uso
 * explícito, não rota). Ponto de encontro, carro e resumo do dia moram em
 * cartões da Home: abrir a Home já os conta; o USO deles está nas tabelas.
 */
export const PRODUCT_FEATURES = [
  'home',
  'roteiro',
  'financeiro',
  'grupo',
  'mapa',
  'bagagem',
  'enquetes',
  'ranking',
  'passaporte',
  'memorias',
  'emergencia',
  'perfil',
  'configuracoes',
  'feedback',
  'sugestoes',
] as const;
export type ProductFeature = (typeof PRODUCT_FEATURES)[number];

export interface ProductEvent {
  /** Gerado no cliente (UUID); é a chave de idempotência: reenviar não duplica. */
  eventId: string;
  eventName: ProductEventName;
  /** Só em `feature_opened`. */
  feature?: ProductFeature;
  /** Relógio do aparelho (ISO). O servidor grava também o `createdAt` dele. */
  occurredAt: string;
  /** Commit do bundle que gerou o evento. */
  clientRelease: string;
  online: boolean;
  standalone: boolean;
  /** Viagem ativa indicada pelo app; o servidor só a mantém se houver participação. */
  tripId?: string;
}

export const PRODUCT_EVENT_KEYS: readonly (keyof ProductEvent)[] = [
  'eventId',
  'eventName',
  'feature',
  'occurredAt',
  'clientRelease',
  'online',
  'standalone',
  'tripId',
];

export const LIMITES_TELEMETRIA = {
  /** Eventos por requisição de ingestão. */
  loteMax: 20,
  /** Fila local: itens e idade máxima. */
  filaMax: 100,
  filaTtlMs: 7 * 24 * 60 * 60_000,
  /** `app_opened` de novo só depois deste tempo em segundo plano. */
  appOpenedIntervaloMs: 30 * 60_000,
  /** Mesma pessoa + mesma área + mesma viagem dentro desta janela = um evento só. */
  featureDedupeMs: 5 * 60_000,
  /** Retenção no servidor (viagem + análise). */
  retencaoDias: 120,
  /**
   * Janela que o SERVIDOR aceita para `occurredAt`, em relação ao relógio
   * dele na ingestão. O instante vem do aparelho (evento offline precisa
   * preservar quando foi observado), mas um relógio quebrado não pode poluir
   * DAU e recorrência por meses: passado até 8 dias (a fila local vive 7, mais
   * um dia de folga para o envio), futuro até 15 min (deriva de relógio).
   */
  occurredAtPassadoMaxMs: 8 * 24 * 60 * 60_000,
  occurredAtFuturoMaxMs: 15 * 60_000,
} as const;

/* ───────────────────────────── validação ───────────────────────────── */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const RELEASE_RE = /^[A-Za-z0-9._-]{1,64}$/;

export type ResultadoEvento = { ok: true; evento: ProductEvent } | { ok: false; motivo: string };

/** Aceita SÓ a allowlist, com tipo exato. Chave desconhecida recusa tudo. */
export function validarEventoDeProduto(corpo: unknown): ResultadoEvento {
  if (!corpo || typeof corpo !== 'object' || Array.isArray(corpo)) return { ok: false, motivo: 'evento inválido' };
  const b = corpo as Record<string, unknown>;
  for (const chave of Object.keys(b)) {
    if (!(PRODUCT_EVENT_KEYS as readonly string[]).includes(chave)) return { ok: false, motivo: `campo não permitido: ${chave.slice(0, 40)}` };
  }
  const eventId = b['eventId'];
  if (typeof eventId !== 'string' || !UUID_RE.test(eventId)) return { ok: false, motivo: 'eventId inválido' };
  const eventName = b['eventName'];
  if (typeof eventName !== 'string' || !(PRODUCT_EVENT_NAMES as readonly string[]).includes(eventName)) return { ok: false, motivo: 'eventName inválido' };
  const occurredAt = b['occurredAt'];
  if (typeof occurredAt !== 'string' || Number.isNaN(Date.parse(occurredAt))) return { ok: false, motivo: 'occurredAt inválido' };
  const clientRelease = b['clientRelease'];
  if (typeof clientRelease !== 'string' || !RELEASE_RE.test(clientRelease)) return { ok: false, motivo: 'clientRelease inválido' };
  if (typeof b['online'] !== 'boolean') return { ok: false, motivo: 'online inválido' };
  if (typeof b['standalone'] !== 'boolean') return { ok: false, motivo: 'standalone inválido' };

  const feature = b['feature'];
  if (eventName === 'feature_opened') {
    if (typeof feature !== 'string' || !(PRODUCT_FEATURES as readonly string[]).includes(feature)) return { ok: false, motivo: 'feature inválida' };
  } else if (feature !== undefined) {
    return { ok: false, motivo: 'feature só em feature_opened' };
  }
  const tripId = b['tripId'];
  if (tripId !== undefined && (typeof tripId !== 'string' || !UUID_RE.test(tripId))) return { ok: false, motivo: 'tripId inválido' };

  const evento: ProductEvent = {
    eventId: eventId.toLowerCase(),
    eventName: eventName as ProductEventName,
    occurredAt: new Date(occurredAt).toISOString(),
    clientRelease,
    online: b['online'] as boolean,
    standalone: b['standalone'] as boolean,
  };
  if (eventName === 'feature_opened') evento.feature = feature as ProductFeature;
  if (typeof tripId === 'string') evento.tripId = tripId.toLowerCase();
  return { ok: true, evento };
}

export type ResultadoLote = { ok: true; eventos: ProductEvent[] } | { ok: false; motivo: string };

/** Um lote é uma lista de 1..`loteMax` eventos; um item ruim recusa o lote. */
export function validarLoteDeEventos(corpo: unknown, max: number = LIMITES_TELEMETRIA.loteMax): ResultadoLote {
  if (!Array.isArray(corpo)) return { ok: false, motivo: 'lote deve ser uma lista' };
  if (corpo.length === 0) return { ok: false, motivo: 'lote vazio' };
  if (corpo.length > max) return { ok: false, motivo: `lote maior que ${max}` };
  const eventos: ProductEvent[] = [];
  for (const [i, item] of corpo.entries()) {
    const r = validarEventoDeProduto(item);
    if (!r.ok) return { ok: false, motivo: `evento ${i + 1}: ${r.motivo}` };
    eventos.push(r.evento);
  }
  return { ok: true, eventos };
}

/* ───────────────────────────── navegação → área ───────────────────────────── */

const ROTA_PARA_FEATURE: readonly [prefixo: string, feature: ProductFeature][] = [
  ['/roteiro', 'roteiro'],
  ['/financeiro', 'financeiro'],
  ['/grupo', 'grupo'],
  ['/mapa', 'mapa'],
  ['/bagagem', 'bagagem'],
  ['/enquetes', 'enquetes'],
  ['/ranking', 'ranking'],
  ['/passaporte', 'passaporte'],
  ['/memorias', 'memorias'],
  ['/emergencia', 'emergencia'],
  ['/perfil', 'perfil'],
  ['/viagem/configuracoes', 'configuracoes'],
  ['/sugestoes', 'sugestoes'],
];

/** A área que uma rota representa, ou `null` para o que não é área (menu, auth, erro). */
export function featureDaRota(pathname: string): ProductFeature | null {
  if (pathname === '/') return 'home';
  for (const [prefixo, feature] of ROTA_PARA_FEATURE) {
    if (pathname === prefixo || pathname.startsWith(`${prefixo}/`)) return feature;
  }
  return null;
}
